import { Button, Heading, Input, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type User, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import {
	APIError,
	selectOrganizationAccessUserMapping,
	selectTeamById,
	selectUsersOfOrganization,
	selectUsersOfTeam,
} from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { AddUserDropdownMenu } from "../../components/DropdownMenus/AddUserDropdownMenu"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useTeamUsersTable, useTeamUsersTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { userFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/teams/$teamId/members")

export const TeamSettingsMembersView = memo(() => {
	const { teamId, organizationId } = routeApi.useParams()

	const team = useAppSelector(selectTeamById(teamId))

	if (!team) throw new Error(`Expected team to be defined, but got ${team}`)

	const organizationUsers = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const teamUsers = useShallowEqaulSelector(selectUsersOfTeam(teamId))
	const organizationAccessByUser = useAppSelector(selectOrganizationAccessUserMapping)

	const nonTeamUsers = useMemo(() => {
		const userIds = new Set(teamUsers.map((user) => user.id))
		return organizationUsers.filter((user) => !userIds.has(user.id))
	}, [organizationUsers, teamUsers])

	const handleAddUserToTeam = useCallback(
		(user: User) => {
			const [_, promise] = sdk.teamMemberships.add({
				team: team.uuid,
				user: user.id,
				organization_access: organizationAccessByUser[user.id].uuid,
			})
			promise.catch((error) => {
				toast.error({
					title: "Failed to add user to team",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[organizationAccessByUser, team.uuid],
	)

	const handleAddAllUsersToTeam = useCallback(
		(users: User[]) => {
			void sdk.teamMemberships.bulkAdd(
				users.map((user) => ({
					team: team.uuid,
					user: user.id,
					organization_access: organizationAccessByUser[user.id].uuid,
				})),
			)
		},
		[organizationAccessByUser, team.uuid],
	)

	const [filteredTeamUsers, filterValue, setFilterValue] = useTextFilter(teamUsers, userFilterCallback)

	const columns = useTeamUsersTableColumns(team)

	const table = useTeamUsersTable({
		data: filteredTeamUsers,
		columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{team.name} members</Heading>
					<Text size="sm" accentColor="base">
						Manage team members.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 grow flex-col gap-2">
					<div className="flex w-full items-center justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<AddUserDropdownMenu
								users={nonTeamUsers}
								onSelectUser={handleAddUserToTeam}
								onSelectAllUsers={handleAddAllUsersToTeam}
								disabled={nonTeamUsers.length === 0}
								size="sm"
							>
								<Button variant="surface" size="sm">
									<LuIcon icon={modelIconMapping.add} />
									Add
								</Button>
							</AddUserDropdownMenu>
						</OrganizationAccessRequired>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							variant="surface"
							size="sm"
							table={{ ...table }}
							interactive={false}
							emptyText="No users."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
TeamSettingsMembersView.displayName = "TeamSettingsMembersView"
